/**
 * Runtime provider for the embed iframe (widget + extension).
 *
 * Uses the same useRuntimeCore hook as the main app (RuntimeProvider.tsx),
 * but with embed-specific auth headers and source metadata.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AssistantRuntimeProvider, useThreadList } from '@assistant-ui/react';
import { apiFetch } from '@/client/lib/api';
import { getApiBasePath, getThreadState } from '@/client/lib/chatApi';
import { useAuth } from '@/client/app/providers/auth-context';
import { FilesProvider } from '@/client/lib/filesContext';
import { ExternalAssetsProvider } from '@/client/lib/assetsContext';
import { PlanProvider } from '@/client/lib/planContext';
import { TasksProvider } from '@/client/lib/tasksContext';
import { TaskDecisionsProvider } from '@/client/components/chat/TaskReview';
import { StreamingStatusProvider } from '@/client/lib/streamingStatusContext';
import { CelebrationProvider } from '@/client/lib/celebrationContext';
import { ToolUiRegistrations } from '@/client/components/assistant-ui/ToolUiRegistrations';
import { getSuggestionsForPage } from '@/client/lib/chatSuggestions';
import { SuggestionsProvider } from '@/client/lib/suggestionsContext';
import { JoinActiveStream } from '@/client/components/chat/JoinActiveStream';
import { ErrorScreen } from '@/client/app/components/ErrorScreen';
import { useRuntimeCore } from '@/client/lib/useRuntimeCore';

// Context for setting thread-creation metadata (mirrors RuntimeProvider pattern)
const ThreadMetadataContext = createContext<(meta: Record<string, unknown>) => void>(() => {});

// Context for the user role fetched from /assistant
const UserRoleContext = createContext<string | null>(null);
export const useUserRole = () => useContext(UserRoleContext);
export const useSetThreadCreationMetadata = () => useContext(ThreadMetadataContext);

interface AppError {
  type: 'forbidden';
  email?: string;
}

export function RuntimeProvider({ children }: { children: ReactNode }) {
  const [assistantId, setAssistantId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [appError, setAppError] = useState<AppError | null>(null);
  const appAuth = useAuth();

  // Fetch assistantId from server
  useEffect(() => {
    apiFetch<{ assistantId: string; role?: string }>(`${getApiBasePath()}/assistant`)
      .then((res) => {
        setAssistantId(res.assistantId);
        if (res.role) setUserRole(res.role);
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : '';
        if (message.includes('403')) {
          let email = appAuth.userEmail ?? undefined;
          try {
            const json = JSON.parse(message.replace(/^API 403: /, ''));
            if (json.email) email = json.email;
          } catch {
            /* ignore */
          }
          setAppError({ type: 'forbidden', email });
        } else {
          console.error('Failed to fetch assistant ID:', err);
        }
      });
  }, [appAuth.userEmail]);

  if (appError) {
    return <ErrorScreen email={appError.email} />;
  }

  // Show auth error
  if (appAuth.authError) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 p-6 text-center text-sm text-muted-foreground">
        <p>Authentication failed: {appAuth.authError}</p>
        <button onClick={() => window.location.reload()} className="text-primary underline">
          Retry
        </button>
      </div>
    );
  }

  const resolvedAssistantId = assistantId;
  if (!resolvedAssistantId) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
        <p>Connecting...</p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
        >
          Reload
        </button>
      </div>
    );
  }

  return (
    <UserRoleContext.Provider value={userRole}>
      <FilesProvider>
        <ExternalAssetsProvider>
          <PlanProvider>
            <TasksProvider>
              <StreamingStatusProvider>
                <CelebrationProvider>
                  <RuntimeInner assistantId={resolvedAssistantId}>
                    {children}
                  </RuntimeInner>
                </CelebrationProvider>
              </StreamingStatusProvider>
            </TasksProvider>
          </PlanProvider>
        </ExternalAssetsProvider>
      </FilesProvider>
    </UserRoleContext.Provider>
  );
}

function RuntimeInner({
  assistantId,
  children,
}: {
  assistantId: string;
  children: ReactNode;
}) {
  const appAuth = useAuth();
  const appAuthRef = useRef(appAuth);
  useEffect(() => {
    appAuthRef.current = appAuth;
  }, [appAuth]);

  // Ref so the adapter's onThreadDeleted can call switchToNewThread after
  // the runtime is created (set below after useRuntimeCore returns).
  const switchRef = useRef<(() => void) | null>(null);

  const pageSuggestions = useMemo(
    () => getSuggestionsForPage(appAuth.pageUrl),
    [appAuth.pageUrl],
  );

  const { runtime, aui, joinSignalRef, setThreadCreationMetadata, extractFromValues } = useRuntimeCore({
    assistantId,
    adapterOptions: {
      onThreadDeleted: () => {
        switchRef.current?.();
      },
    },
    suggestions: pageSuggestions,
    onBeforeSend: () => {
      // Request a fresh token from the parent page before each agent run
      // so the token doesn't expire during long-running tasks.
      appAuth.requestTokenRefresh();
    },
    getSendExtras: () => {
      let skillBranch: string | undefined;
      try {
        skillBranch = localStorage.getItem('gaia-embed-skill-branch') || undefined;
      } catch {
        /* private browsing */
      }
      return {
        pageUrl: appAuthRef.current.pageUrl || document.referrer || '',
        ...(skillBranch ? { agentMetadata: { skills_branch: skillBranch } } : {}),
      };
    },
    loadErrorLabel: 'Failed to load embed thread state:',
  });
  useEffect(() => {
    switchRef.current = () => (runtime as unknown as { threads: { switchToNewThread: () => void } }).threads.switchToNewThread();
  }, [runtime]);

  const extractRef = useRef(extractFromValues);
  useEffect(() => { extractRef.current = extractFromValues; }, [extractFromValues]);

  return (
    <ThreadMetadataContext.Provider value={setThreadCreationMetadata}>
      <AssistantRuntimeProvider aui={aui} runtime={runtime}>
        <JoinActiveStream signalRef={joinSignalRef} />
        <ThreadStateSync extractFromValuesRef={extractRef} />
        <TaskDecisionsProvider>
          <SuggestionsProvider suggestions={pageSuggestions}>
            <ToolUiRegistrations />
            {children}
          </SuggestionsProvider>
        </TaskDecisionsProvider>
      </AssistantRuntimeProvider>
    </ThreadMetadataContext.Provider>
  );
}

/**
 * Syncs thread state values (plan, assets, files) when the active thread changes.
 * Fetches getThreadState() independently of assistant-ui's load() callback,
 * which may not be called for cached/previously-loaded threads.
 */
function ThreadStateSync({
  extractFromValuesRef,
}: {
  extractFromValuesRef: React.RefObject<(values: unknown, threadId?: string) => void>;
}) {
  const mainThreadId = useThreadList((s) => s.mainThreadId);
  const threadItems = useThreadList((s) => s.threadItems);
  const currentThread = threadItems[mainThreadId];
  const externalId = currentThread?.externalId ?? currentThread?.remoteId;

  const prevExternalIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (externalId === prevExternalIdRef.current) return;
    prevExternalIdRef.current = externalId;

    if (!externalId) {
      // New thread — clear state
      extractFromValuesRef.current?.({}, undefined);
      return;
    }

    // Fetch state values for the new thread
    let cancelled = false;
    getThreadState(externalId)
      .then((state) => {
        if (cancelled) return;
        extractFromValuesRef.current?.(state.values, externalId);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('ThreadStateSync: failed to fetch state for', externalId, err);
      });

    return () => { cancelled = true; };
  }, [externalId, extractFromValuesRef]);

  return null;
}

